import { Alert, AlertTitle, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

type Severity = "info" | "error" | "success"

// MUI Alert does not have a "success" icon variant — map it to "info" to reuse its icon
const MUI_SEVERITY: Record<Severity, "info" | "error"> = {
    info: "info",
    error: "error",
    success: "info",
}

const useStyles = makeStyles()((theme) => ({
    alert: {
        width: "fit-content",
    },
    alertTitle: {
        margin: 0,
    },

    alertInfo: {
        background: theme.palette.tailwind.sky[50],
        borderRadius: ".5rem",
    },
    alertTitleInfo: {
        color: theme.palette.tailwind.sky[700],
        fontWeight: "normal",
    },

    alertError: {
        background: theme.palette.tailwind.red[600],
        borderRadius: ".25rem",
        "& .MuiAlert-icon": {
            color: theme.palette.common.white,
        },
        "& .MuiAlert-message": {
            color: theme.palette.common.white,
        },
    },
    alertTitleError: {
        color: theme.palette.common.white,
        fontWeight: "normal",
    },

    alertSuccess: {
        background: theme.palette.tailwind.green[100],
        borderRadius: ".5rem",
        "& .MuiAlert-icon": {
            color: theme.palette.tailwind.green[700],
        },
    },
    alertTitleSuccess: {
        color: theme.palette.tailwind.green[700],
        fontWeight: "normal",
    },
}))

type StandardAlertInfoProps = Readonly<{
    title: string
    icon?: boolean
    className?: string
    severity?: Severity
    useDefaultStyles?: boolean
}>

export function StandardAlertInfo({
    title,
    icon,
    className,
    severity = "info",
    useDefaultStyles = false,
}: StandardAlertInfoProps) {
    const { classes, cx } = useStyles()

    const alertClasses: Record<Severity, string> = {
        info: classes.alertInfo,
        error: classes.alertError,
        success: classes.alertSuccess,
    }

    const titleClasses: Record<Severity, string> = {
        info: classes.alertTitleInfo,
        error: classes.alertTitleError,
        success: classes.alertTitleSuccess,
    }

    const alertClass = useDefaultStyles ? undefined : alertClasses[severity]
    const titleClass = useDefaultStyles ? undefined : titleClasses[severity]

    return (
        <Alert severity={MUI_SEVERITY[severity]} icon={icon} className={cx(classes.alert, alertClass, className)}>
            <AlertTitle className={cx(classes.alertTitle, titleClass)}>
                <Typography variant="body2">{title}</Typography>
            </AlertTitle>
        </Alert>
    )
}
